import { useCallback, useState } from 'react';
import { BLEService } from 'web/services/BLEService';
import { useBleDeviceStore } from 'web/stores/bleDeviceStore';
import { log } from 'web/utils/logger';
import { AnimoLabel, LabelDataUtil } from 'web/utils/LabelDataUtil';
import { collectDeviceMetadata } from 'web/utils/DeviceMetadataUtil';
import { useAddL1Measurement, useAddLabelIntake, useAddNxDevice, useAddPatientNote, usePostInStream } from 'web/services/api/queries';
import { useGetDefaultPatient } from 'web/services/api/queries';
import { Label, LabelType } from 'web/model/Label';
import { PatientNote } from 'web/model/PatientNote';
import { PatientNoteType } from 'web/model/PatientNoteType';
import { useQueryClient } from 'react-query';

type NotificationType = 'error' | 'warning' | 'success' | 'info';
type Notification = {
    message: string;
    type: NotificationType;
};

export const useBleDeviceSyncWithoutLabels = (
    onSyncComplete?: () => void
) => {
    const { connect, disconnect } = useBleDeviceStore();
    const addNxDevice = useAddNxDevice();
    const postInStream = usePostInStream();
    const [notification, setNotification] = useState<Notification | null>(null);
    const [labels, setLabels] = useState<(AnimoLabel & { patientId?: string })[]>([]);
    const [_, setDeviceSerial] = useState<string | undefined>();
    const [progress, setProgress] = useState<number>(0);
    const [isSyncing, setIsSyncing] = useState<boolean>(false);
    const [fileCount, setFileCount] = useState<number>(0);
    const addLabelIntake = useAddLabelIntake();
    const addPatientNote = useAddPatientNote();
    const addL1Measurement = useAddL1Measurement();
    const defaultPatientQuery = useGetDefaultPatient();
    const queryClient = useQueryClient();

    // Store BLE device info for metadata
    const [deviceBatteryLevel, setDeviceBatteryLevel] = useState<number | undefined>();
    const [deviceFirmwareVersion, setDeviceFirmwareVersion] = useState<string | undefined>();
    const [syncedDeviceId, setSyncedDeviceId] = useState<string | undefined>();
    const [syncedDeviceFirmware, setSyncedDeviceFirmware] = useState<string | undefined>();

    const showNotification = useCallback(
        (message: string, type: NotificationType = 'error', autoClear: boolean = true) => {
            setNotification({ message, type });
            if (autoClear) {
                setTimeout(() => setNotification(null), 5000);
            }
        },
        []
    );

    const handleSyncDevice = useCallback(
        async (device: BluetoothDevice, isCreatedByOnboarding: boolean) => {
            let syncSuccessful = false;
            let syncAttempts = 0;
            const MAX_SYNC_ATTEMPTS = 2;

            const attemptSync = async (): Promise<boolean> => {
                try {
                    setIsSyncing(true);
                    setProgress(0);
                    setLabels([]);
                    setFileCount(0);
                    setSyncedDeviceId(undefined);
                    setSyncedDeviceFirmware(undefined);

                    log('Starting device sync without labels');
                    const bleDevice = device;

                    const deviceSerial = bleDevice.name ?? 'TEST_DEVICE';
                    setDeviceSerial(deviceSerial);

                    log('Device connected:', deviceSerial);
                    showNotification(`Connecting to device ${deviceSerial}...`, 'info', false);

                    await connect(bleDevice);
                    showNotification(`Connected to device ${deviceSerial}`, 'success');

                    try {
                        await addNxDevice.mutateAsync(deviceSerial);
                    } catch (error) {
                        console.error('Error adding NX device:', error);
                    }

                    const isNxHub = deviceSerial?.toUpperCase().startsWith('NXHUB');

                    // 1. Sync epoch time (skip for NXHUB devices - they don't have this characteristic)
                    if (!isNxHub) {
                        log('Syncing device time...');
                        showNotification('Syncing device time...', 'info', false);
                        await BLEService.syncEpochTime(bleDevice);
                        showNotification('Device time synchronized successfully', 'success');
                    }

                    // 2. Check battery level and firmware version (skip battery for NXHUB devices)
                    let batteryLevel: number | undefined;
                    if (!isNxHub) {
                        log('Checking battery level...');
                        showNotification('Checking battery status...', 'info', false);
                        batteryLevel = await BLEService.readBatteryLevel(bleDevice);
                        log('Battery level:', `${batteryLevel.toFixed(2)}V`);
                        showNotification(`Battery level: ${batteryLevel.toFixed(2)}V`, 'success');

                        // Store battery level for metadata
                        setDeviceBatteryLevel(batteryLevel);

                        if (BLEService.checkLowBattery(batteryLevel)) {
                            showNotification(`⚠️ Low battery warning: ${batteryLevel.toFixed(2)}V`, 'warning');
                        }
                    }

                    log('Reading firmware version...');
                    showNotification('Reading firmware version...', 'info', false);

                    const firmwareVersion = await BLEService.readFirmwareVersion(bleDevice);
                    log('Firmware version:', firmwareVersion);
                    showNotification(`Firmware version: ${firmwareVersion}`, 'success');
                    
                    // Store firmware version for metadata
                    setDeviceFirmwareVersion(firmwareVersion);

                    // 3. Set device to data export mode
                    log('Setting device to data export mode...');
                    showNotification('Setting device to data export mode...', 'info', false);
                    await BLEService.writeDeviceMode(bleDevice, 'MODE_DATA_EXPORT');
                    showNotification('Device mode set to data export', 'success');

                    // 4. Transfer Time Series Files
                    log('Transferring time series files...');
                    showNotification('Transferring time series files...', 'info', false);

                    try {
                        await BLEService.transferTimeSeriesFiles(
                            bleDevice,
                            async (file: File) => {
                                setFileCount(prev => prev + 1);
                                showNotification(`Received file: ${file.name}`, 'info');
                                await postInStream.mutateAsync({
                                    file,
                                    deviceSerial: bleDevice.name ?? "TEST_DEVICE"
                                });
                                log('Received file:', file.name);
                            },
                            (fileId: number, progressValue: number) => {
                                setProgress(progressValue);
                                if (progressValue < 100) {
                                    showNotification(`Transferring file ${fileId}: ${progressValue.toFixed(1)}%`, 'info', false);
                                } else {
                                    showNotification(`File ${fileId} transfer complete`, 'success');
                                }
                            }
                        );
                    } catch (error) {
                        console.error('Error during time series transfer:', error);
                        showNotification(`Time series transfer issue: ${(error as Error).message}. Continuing with sync...`, 'warning');
                    }

                    // 5. Set device to standby mode
                    log('Setting device to standby mode...');
                    showNotification('Setting device to standby mode...', 'info', false);
                    await BLEService.writeDeviceMode(bleDevice, 'MODE_STANDBY');

                    // Read and verify current mode
                    const currentMode = await BLEService.readDeviceMode(bleDevice);
                    showNotification(`Device mode set to: ${currentMode}`, 'success');

                    // 6. Read available labels
                    log('Reading available labels from device...');
                    showNotification('Reading available labels from device...', 'info');
                    const numberOfLabels = await BLEService.readNumberOfLabels(bleDevice);
                    log('Available labels:', numberOfLabels);
                    showNotification(`Found ${numberOfLabels} label(s) on device`, 'info');

                    // 7. Transfer labels
                    if (numberOfLabels > 0) {
                        const defaultPatient = defaultPatientQuery.data;
                        const patientId = defaultPatient?.id;

                        showNotification(`Downloading ${numberOfLabels} label(s)...`, 'info', false);
                        const labels = await BLEService.downloadLabels(bleDevice, numberOfLabels);
                        const processedLabels = [];

                        showNotification(`Processing ${labels.length} label(s)...`, 'info', false);

                        for (let i = 0; i < labels.length; i++) {
                            const label = labels[i];
                            showNotification(`Processing label ${i + 1} of ${labels.length}...`, 'info', false);
                            if (LabelDataUtil.validateAnimoLabel(label)) {
                                // Collect device metadata with BLE device info
                                const deviceMetadata = collectDeviceMetadata(
                                    batteryLevel, // Use the current battery level from this sync
                                    firmwareVersion // Use the current firmware version from this sync
                                );

                                // Create the label with metadata as object
                                const labelId = await addLabelIntake.mutateAsync({
                                    deviceSerial: bleDevice.name ?? "T",
                                    label: new Label({
                                        name: "AUTONOMOUS_MEASUREMENT",
                                        fromTime: label.startTime,
                                        toTime: label.endTime,
                                        color: "PINK",
                                        description: "",
                                        patientId: patientId,
                                        type: LabelType[label.labelType] as keyof typeof LabelType,
                                        isCreatedByOnboarding: isCreatedByOnboarding,
                                        metadata: deviceMetadata // Add metadata as object - JSON stringification happens in API
                                    })
                                });
                                log('Label added:', labelId);

                                // Then add the L1 measurement data
                                if (labelId) {
                                    await addL1Measurement.mutateAsync({
                                        labelId: labelId.id,
                                        l1MeasurementRequest: {
                                            source: "Web",
                                            confidence: 0.0,
                                            remark: "Continuous",
                                            data: {
                                                temperature: label.temperature,
                                                heartRate: label.heartRate,
                                                respiratoryRate: label.respRate,
                                                heartRateConfidence: label.heartRateConfidence,
                                                respRateConfidence: label.respRateConfidence
                                            }
                                        }
                                    });
                                    log('L1 measurement added for label:', labelId);

                                    // Add AI note
                                    await addPatientNote.mutateAsync({
                                        note: new PatientNote({
                                            content: `Heart rate: ${label.heartRate}, Respiratory rate: ${label.respRate}, Temperature: ${label.temperature}`,
                                            time: new Date(label.startTime),
                                            patient: defaultPatient,
                                            ownedByLabelId: labelId.id
                                        }),
                                        type: PatientNoteType.AI
                                    });

                                    // Add detailed note
                                    await addPatientNote.mutateAsync({
                                        note: new PatientNote({
                                            content: `Heart rate: ${label.heartRate}, Heart Rate Confidence: ${label.heartRateConfidence}, Respiratory rate: ${label.respRate}, Resp Rate Confidence: ${label.respRateConfidence}, Temperature: ${label.temperature}`,
                                            time: new Date(label.startTime),
                                            patient: defaultPatient,
                                            ownedByLabelId: labelId.id
                                        }),
                                        type: PatientNoteType.NotSpecified
                                    });

                                    // Store the processed label with its ID
                                    processedLabels.push({
                                        ...label,
                                        patientId: patientId,
                                        labelId: labelId.id
                                    });
                                }
                            }
                        }

                        setLabels(processedLabels);
                    } else {
                        showNotification('No labels found on device', 'warning');
                    }

                    // 8. Clear labels
                    log('Clearing all labels...');
                    showNotification('Clearing all labels from device...', 'info');
                    await BLEService.writeClearAllLabels(bleDevice);
                    showNotification('All labels cleared from device', 'success');

                    // Ensure progress is at 100% after completion
                    setProgress(100);
                    showNotification(`Device sync completed successfully. Transferred ${fileCount} file(s). Refreshing measurements...`, 'success');

                    // 9. Set device to standby mode
                    await BLEService.writeDeviceMode(bleDevice, 'MODE_STANDBY');

                    await disconnect();
                    log('Device sync completed successfully');

                    // Store synced device info for firmware check callback
                    setSyncedDeviceId(bleDevice.id);
                    setSyncedDeviceFirmware(firmwareVersion);

                    return true;
                } catch (error) {
                    console.error('Error during device sync attempt:', error);
                    showNotification(`Sync attempt ${syncAttempts + 1}/${MAX_SYNC_ATTEMPTS} failed: ${(error as Error).message}`, 'error');
                    await disconnect().catch(e => console.error('Error disconnecting:', e));
                    return false;
                }
            };

            try {
                while (syncAttempts < MAX_SYNC_ATTEMPTS) {
                    syncAttempts++;
                    log(`Starting sync attempt ${syncAttempts}/${MAX_SYNC_ATTEMPTS}`);

                    syncSuccessful = await attemptSync();

                    if (syncSuccessful) {
                        log(`Sync successful on attempt ${syncAttempts}`);
                        break;
                    } else if (syncAttempts < MAX_SYNC_ATTEMPTS) {
                        log(`Waiting before retry ${syncAttempts + 1}...`);
                        showNotification(`Waiting 3 seconds before retry...`, 'warning');
                        await new Promise(resolve => setTimeout(resolve, 3000));
                    }
                }

                if (!syncSuccessful) {
                    showNotification(`Sync failed after ${MAX_SYNC_ATTEMPTS} attempts`, 'error');
                }
            } finally {
                setIsSyncing(false);
                setProgress(0);

                if (syncSuccessful) {
                    log('Sync successful, refreshing measurements data');

                    setTimeout(() => {
                        try {
                            log('Invalidating all organization-labels queries');
                            queryClient.invalidateQueries({
                                predicate: (query) => {
                                    const queryKey = query.queryKey;
                                    if (Array.isArray(queryKey) && queryKey[0] === 'organization-labels') {
                                        log('Invalidating query:', queryKey);
                                        return true;
                                    }
                                    return false;
                                }
                            });

                            if (onSyncComplete) {
                                log('Calling onSyncComplete callback');
                                onSyncComplete();
                                log('onSyncComplete callback completed');
                            }
                        } catch (err) {
                            console.error('Error refreshing measurements data:', err);
                        }
                    }, 500);
                } else if (!syncSuccessful) {
                    log('Sync was not successful, not refreshing measurements');
                }
            }
        },
        [connect, disconnect, showNotification, defaultPatientQuery, onSyncComplete, addNxDevice, postInStream, addLabelIntake, addPatientNote, addL1Measurement, queryClient, deviceBatteryLevel, deviceFirmwareVersion]
    );

    return {
        handleSyncDevice,
        labels,
        setLabels,
        notification,
        progress,
        isSyncing,
        syncedDeviceId,
        syncedDeviceFirmware
    };
};